import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import {
  AutomationTimingBlockUpdateDocument,
  AutomationTimingBlockUpdateMutation,
  AutomationTimingBlockUpdateMutationVariables,
  AutomationFragment as AutomationType,
  DurationUnitsEnum
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { optionsForDurationUnits } from '@utils/optionUtils';

type AutomationTimingBlock = Extract<AutomationType['blocks'][number], { __typename: 'AutomationTimingBlock' }>;

type FormData = {
  duration: number;
  durationUnits: DurationUnitsEnum;
};

type Props = {
  automationTimingBlock: AutomationTimingBlock;
  locked: boolean;
};

const AutomationTimingBlockForm = ({ automationTimingBlock, locked }: Props) => {
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const initialValues: FormData = useMemo(
    () => ({
      duration: automationTimingBlock.timingDuration,
      durationUnits: automationTimingBlock.timingDurationUnits
    }),
    [automationTimingBlock.timingDuration, automationTimingBlock.timingDurationUnits]
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      duration: yup
        .number()
        .moreThan(0, intl.formatMessage({ id: 'errors.automationTimingBlock.duration.greater_than' }))
        .required(intl.formatMessage({ id: 'errors.automationTimingBlock.duration.blank' }))
        .typeError(intl.formatMessage({ id: 'errors.automationTimingBlock.duration.number' })),
      durationUnits: yup
        .string()
        .oneOf(
          Object.values(DurationUnitsEnum),
          intl.formatMessage({ id: 'errors.automationTimingBlock.durationUnits.inclusion' })
        )
        .required(intl.formatMessage({ id: 'errors.automationTimingBlock.durationUnits.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const durationUnitsOptions = useMemo(() => optionsForDurationUnits(intl), [intl]);

  const [updateAutomationTimingBlock] = useMutation<
    AutomationTimingBlockUpdateMutation,
    AutomationTimingBlockUpdateMutationVariables
  >(AutomationTimingBlockUpdateDocument);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return updateAutomationTimingBlock({
      variables: {
        ...form.getValues(),
        id: automationTimingBlock.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.automationTimingBlockUpdate,
        intl,
        onError: () => {},
        onSuccess: () => {},
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.automationTimingBlock'
      });
    });
  }, [updateAutomationTimingBlock, form, automationTimingBlock.id, intl]);

  return (
    <Form data-test="automation-timing-block-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Input<FormData>
        control={form.control}
        disabled={locked}
        error={form.formState.errors.duration}
        label={intl.formatMessage({ id: 'forms.automationTimingBlock.durationLabel' })}
        name="duration"
        required
        type="number"
      />
      <Select<FormData>
        control={form.control}
        disabled={locked}
        error={form.formState.errors.durationUnits}
        label={intl.formatMessage({ id: 'forms.automationTimingBlock.durationUnitsLabel' })}
        name="durationUnits"
        options={durationUnitsOptions}
      />
      {!locked && <FormButtons hookForm={form} saveDisabled={!form.formState.isDirty} skipCancel />}
    </Form>
  );
};

export default AutomationTimingBlockForm;
